import { useCallback, useMemo, useState } from 'react';

import { roundTo } from '@/shared/utils/decimal-scale/decimal-scale.util';

export interface UseScalarNumberInput {
  /** The committed value in **display** units — the control converts, not this. */
  displayValue: number | null | undefined;
  /** How many decimal places the domain permits. `0` means whole numbers. */
  decimals?: number;
  /** What one arrow press is worth, in display units. */
  step?: number;
  /** The stepper's bounds, in display units. Typing is NOT clamped to them. */
  min?: number;
  max?: number;
  /** The value must be a whole number of display units — a duration's amount. */
  isIntegerOnly?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  /** `undefined` means "there is no value", which is not the same as `0`. */
  onCommit: (next: number | undefined) => void;
  /** Called when a read-only field turns an edit away, so it can be announced. */
  onRefuse: () => void;
}

export interface UseScalarNumber {
  /** What the box shows: the in-progress draft, or the committed value. */
  text: string;
  /** The draft parsed as a number, even when it could not be committed. */
  draftValue: number | undefined;
  /** The draft is not a value this field can hold. */
  isDraftInvalid: boolean;
  setText: (next: string) => void;
  stepBy: (steps: number) => void;
  /** Drops the draft so the box settles back to the committed value. */
  reset: () => void;
}

/** Arabic-Indic (٠…٩) and Extended Arabic-Indic (۰…۹) digits. */
const ARABIC_DIGITS = /[٠-٩۰-۹]/gu;
const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;

/**
 * The digits an Arabic or Persian keyboard produces, read as the numbers they
 * are. Refusing them would make the portal's DEFAULT language its least usable
 * one, and `Number('٤٢')` is `NaN`.
 */
function toLatinDigits(text: string): string {
  return text.replace(ARABIC_DIGITS, (digit) => {
    const code = digit.codePointAt(0) ?? ARABIC_INDIC_ZERO;
    const zero =
      code >= EXTENDED_ARABIC_INDIC_ZERO ? EXTENDED_ARABIC_INDIC_ZERO : ARABIC_INDIC_ZERO;
    return String(code - zero);
  });
}

/** A draft parsed to a number, or `undefined` when it is not one. */
function parseDraft(text: string): number | undefined {
  const trimmed = toLatinDigits(text).trim();
  if (trimmed === '') return undefined;

  // `Number('')` is 0 and `Number('  ')` is 0, both already handled above;
  // `Number('1,5')` is NaN, which is correct — a comma decimal is a different
  // locale's convention and guessing between "1.5" and "15" would be worse.
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * The numeric core `DurationInput` and `PercentInput` share.
 *
 * Three behaviours, each of which is a bug the moment two controls own a copy:
 *
 *  1. **The draft.** The box keeps its own in-progress text and commits only
 *     what parses, so `0.` survives long enough to become `0.5`. `ColorInput`
 *     learned this first — without it every keystroke fires a value at the form.
 *  2. **Empty is not zero.** A cleared box commits `undefined`; `0` is a real
 *     value for several of the settings these controls serve.
 *  3. **A draft that cannot be stored commits `undefined` too.** Keeping the
 *     previous value instead would leave the form holding 15 while the box shows
 *     something else, and Save would write a number the operator can see is not
 *     what they typed. The schema then refuses it — validity keeps exactly one
 *     source (the lesson `issues` carries in the structured family).
 *
 * Typing is never clamped: snapping "7" to a minimum of 10 on the way to "70"
 * fights the typist. The **stepper** does clamp, because an arrow that walks
 * past a bound is broken rather than permissive.
 */
export function useScalarNumber({
  displayValue,
  decimals = 0,
  step = 1,
  min,
  max,
  isIntegerOnly = false,
  isReadOnly = false,
  isDisabled = false,
  onCommit,
  onRefuse,
}: UseScalarNumberInput): UseScalarNumber {
  // `null` means "not editing": the box then shows the committed value, so a
  // change from outside is picked up immediately.
  const [draft, setDraft] = useState<string | null>(null);

  /**
   * The ONE refusal choke point. Every mutating entry passes through here, so a
   * read-only field announces exactly once and mutates nothing, and a disabled
   * one stays silent — a disabled control owes no explanation, it is already
   * unmistakably out of service.
   */
  const refuse = useCallback((): boolean => {
    if (isDisabled) return true;
    if (!isReadOnly) return false;
    onRefuse();
    return true;
  }, [isDisabled, isReadOnly, onRefuse]);

  const committedText =
    displayValue === null || displayValue === undefined ? '' : String(displayValue);

  const draftValue = draft === null ? undefined : parseDraft(draft);

  const isDraftInvalid =
    draft !== null &&
    draft.trim() !== '' &&
    (draftValue === undefined || (isIntegerOnly && !Number.isInteger(draftValue)));

  const setText = useCallback(
    (next: string) => {
      if (refuse()) return;
      setDraft(next);

      if (next.trim() === '') {
        onCommit(undefined);
        return;
      }

      const parsed = parseDraft(next);
      if (parsed === undefined || (isIntegerOnly && !Number.isInteger(parsed))) {
        onCommit(undefined);
        return;
      }

      onCommit(roundTo(parsed, decimals));
    },
    [decimals, isIntegerOnly, onCommit, refuse],
  );

  const stepBy = useCallback(
    (steps: number) => {
      if (refuse()) return;

      // An empty field steps to its floor rather than to `step`: the first press
      // should land on a legal value, not on an arbitrary one.
      const base = draftValue ?? displayValue ?? (min ?? 0) - step;
      const next = roundTo(base + steps * step, decimals);
      const clamped = Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? -Infinity, next));

      setDraft(null);
      onCommit(clamped);
    },
    [decimals, displayValue, draftValue, max, min, onCommit, refuse, step],
  );

  const reset = useCallback(() => {
    setDraft(null);
  }, []);

  return useMemo(
    () => ({
      text: draft ?? committedText,
      draftValue,
      isDraftInvalid,
      setText,
      stepBy,
      reset,
    }),
    [committedText, draft, draftValue, isDraftInvalid, reset, setText, stepBy],
  );
}
